import { baseUrl, getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
    isActive: boolean;
    useAlternativeArticleNumber: boolean;
    useSupplierArticleNumber: boolean;
    isGroupSaleBrand: boolean;
    company: Company | null;
    companies: Company[];
    brandCategory: BrandCategory | null;
    barcodeType: BarcodeType | null;
    salesChannels: SalesChannel[];
    itemGroups: ItemGroup[];
    brandConnectIdentifier: string | null;
    brandConnect: BrandConnectInfo | null;
}

export interface BrandConnectInfo {
    supplierTenant: string;
    supplierName: string;
    status: string;
    connectedLocations: string[] | null;
}

export interface Company {
    id: number;
    companyName: string;
}

export interface BrandCategory {
    id: number;
    typeName: string;
    isBarcodeGenerationAllowed: boolean;
}

export interface BarcodeType {
    id: number;
    barcodeTypeName: string;
}

export interface SalesChannel {
    id: number;
    salesChannelCode: string;
    salesChannelDescription: string;
}

export interface MainGroup {
    id: number;
    mainGroupCode: string;
    mainGroupDescription: string;
}

export interface ItemGroup {
    mainGroup: MainGroup;
    showEditForm?: boolean;
}

export interface Location {
    id: number;
    locationNumber: number;
    locationName: string;
}

export interface TenantConfig {
    articleBrandCodeLength: number;
    assignedBrandCodes: string[];
}

export interface VariantGroup {
    id: number;
    variantGroupName: string;
}

export interface Vat {
    id: number;
    vatPercent: number;
}

// API calls

export async function getBrand(brandId: number): Promise<Brand> {
    const result = await rms2Fetch<Brand>(`brands/${brandId}`);
    if (!result) throw new Error('Empty response for getBrand');
    return result;
}

export async function getCompanies(): Promise<Company[]> {
    const response = await rms2Fetch<{ companies: { id: number; companyName: string }[] }>('companies');
    return response.companies.map(c => ({ id: c.id, companyName: c.companyName }));
}

export async function getBrandCategories(): Promise<BrandCategory[]> {
    const response = await rms2Fetch<{ brandCategories: BrandCategory[] }>('brandcategories');
    return response.brandCategories;
}

export async function getBarcodeTypes(): Promise<BarcodeType[]> {
    const response = await rms2Fetch<{ barcodeTypes: BarcodeType[] }>('barcodetypes');
    return response.barcodeTypes;
}

export async function getSalesChannels(): Promise<SalesChannel[]> {
    const response = await rms2Fetch<{ salesChannels: SalesChannel[] }>('saleschannels');
    return response.salesChannels;
}

export async function getMainGroups(): Promise<MainGroup[]> {
    const response = await rms2Fetch<{ mainGroups: MainGroup[] }>('maingroups');
    return response.mainGroups;
}

export async function getLocations(): Promise<Location[]> {
    const response = await rms2Fetch<{ locations: { id: number; number: number; name: string }[] }>('locations');
    return response.locations.map(l => ({ id: l.id, locationNumber: l.number, locationName: l.name }));
}

export async function getTenantConfig(): Promise<TenantConfig> {
    const result = await legacyFetch<TenantConfig>('brands/tenantconfig');
    if (!result) throw new Error('Empty response for getTenantConfig');
    return result;
}

export async function getVariantGroups(): Promise<VariantGroup[]> {
    const response = await rms2Fetch<{ variantGroups: { id: number; groupName: string }[] }>('variantgroups');
    return response.variantGroups.map(g => ({ id: g.id, variantGroupName: g.groupName }));
}

export async function isBrandConnectEnabled(): Promise<boolean> {
    try {
        const response = await rms2Fetch<boolean>('features/FeatureBrandConnect');
        return response ?? false;
    } catch {
        return false;
    }
}

export async function updateBrand(brand: Brand, mainGroups: MainGroup[], addedCompanies: Company[], removedCompanies: Company[]): Promise<Brand> {
    const result = await legacyFetch<Brand>('brands', {
        method: 'PUT',
        body: JSON.stringify({ brand, mainGroups, addedCompanies, removedCompanies })
    });
    if (!result) throw new Error('Empty response for updateBrand');
    return result;
}

export async function createBrand(brand: Partial<Brand>, mainGroups: MainGroup[], addedCompanies: Company[], removedCompanies: Company[]): Promise<Brand> {
    const result = await legacyFetch<Brand>('brands', {
        method: 'POST',
        body: JSON.stringify({ brand, mainGroups, addedCompanies, removedCompanies })
    });
    if (!result) throw new Error('Empty response for createBrand');
    return result;
}

export async function createItemGroups(mainGroups: MainGroup[], brands: Brand[]): Promise<void> {
    await rms2Fetch<void>('maingroups/createitemgroups', {
        method: 'POST',
        body: JSON.stringify({
            mainGroupIds: mainGroups.map(mg => mg.id),
            brandIds: brands.map(b => b.id),
        })
    });
}

export async function connectBrandSupplier(brandId: number, supplierTenant: string, locationIds: number[]): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/supplier`, {
        method: 'POST',
        body: JSON.stringify({ supplierTenant, locationIds })
    });
}

export async function disconnectBrandSupplier(brandId: number): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/supplier`, {
        method: 'DELETE'
    });
}

export async function setBrandConnectIdentifier(brandId: number, brandConnectIdentifier: string): Promise<void> {
    await rms2Fetch<void>(`brands/${brandId}/brandconnect/identifier`, {
        method: 'PUT',
        body: JSON.stringify({ brandConnectIdentifier })
    });
}
